import { useCallback } from 'react';
import {
  type NavigateOptions,
  createSearchParams,
  useSearchParams,
} from 'react-router-dom';

type UseQueryParams = {
  queryParams: URLSearchParams;
  setQueryParams: (
    params: Record<string, string | null>,
    options?: NavigateOptions
  ) => void;
  removeQueryParam: (
    param: string | string[],
    options?: NavigateOptions
  ) => void;
};

export const useQueryParams = (): UseQueryParams => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setQueryParams = useCallback(
    (params: Record<string, string | null>, options?: NavigateOptions) => {
      const newParams = createSearchParams({
        ...Object.fromEntries(searchParams),
        ...(params as Record<string, string>),
      });
      // A null value means "remove this param" (createSearchParams stringifies
      // it to 'null'). Collect the keys first, then delete — deleting while
      // iterating skips the entry after each removed one, so adjacent cleared
      // params would otherwise survive as a literal 'null' string.
      const keysToDelete = [...newParams]
        .filter(([, value]) => value === 'null')
        .map(([key]) => key);
      keysToDelete.forEach((key) => newParams.delete(key));
      setSearchParams(newParams, options);
    },
    [searchParams, setSearchParams]
  );

  const removeQueryParam = useCallback(
    (param: string | string[], options?: NavigateOptions) => {
      if (!Array.isArray(param)) {
        param = [param];
      }
      const newParams = createSearchParams(Object.fromEntries(searchParams));
      param.forEach((key) => newParams.delete(key));
      setSearchParams(newParams, options);
    },
    [searchParams, setSearchParams]
  );

  return {
    queryParams: searchParams,
    setQueryParams,
    removeQueryParam,
  };
};
